import type {
  WSCallConnectedData,
  WSCallHeartbeatData,
  WSCallFinalizedData,
  WSCallHistoryMarkMissedSeenData,
  WSCallHistorySyncAckData,
  WSCallHistorySyncResultData
} from '@shared/types';
import { callsAPI } from '../api/calls';
import { signPayloadWithKey } from '../crypto/messageSigning';
import { useAuthStore } from '../store/authStore';
import { useCallHistoryStore } from '../store/callHistoryStore';
import { finalizePagedSync } from './pagedSync';
import { resolveStrictMessagingLocalContext } from './actionContextResolver';
import { wsClient } from './websocket';

let initialized = false;

async function getCallHistoryContext(localIdentityId?: string) {
  const connectionIdentityId = (localIdentityId || wsClient.getConnectionContext()?.identityId || '').toString().trim();
  if (!connectionIdentityId) return null;

  const context = await resolveStrictMessagingLocalContext({
    localIdentityId: connectionIdentityId,
    timeoutMs: 10_000
  });

  const state = useAuthStore.getState();
  const identity = state.identities[context.localIdentityId];
  if (!identity) return null;

  return {
    deviceId: context.localDeviceId,
    identity
  };
}

function getCallHistoryHttpContext(localIdentityId?: string) {
  const state = useAuthStore.getState();
  const requestedIdentityId = (localIdentityId || '').toString().trim();
  const connectionIdentityId = (wsClient.getConnectionContext()?.identityId || '').toString().trim();
  const currentDeviceId = (state.currentDeviceId || '').toString().trim();
  const currentIdentityId = currentDeviceId ? (state.devices[currentDeviceId]?.identityId || '').toString().trim() : '';
  const identityId = requestedIdentityId || connectionIdentityId || currentIdentityId;
  if (!identityId) return null;

  const identity = state.identities[identityId];
  if (!identity) return null;
  const device = Object.values(state.devices).find((entry) => entry.identityId === identityId);
  if (!device) return null;
  return {
    deviceId: device.deviceId,
    identity
  };
}

async function applyCallHistorySyncResult(data: WSCallHistorySyncResultData, localIdentityId?: string): Promise<void> {
  const state = useAuthStore.getState();
  const identityId = (localIdentityId || wsClient.getConnectionContext()?.identityId || '').toString().trim();
  const identity = identityId ? state.identities[identityId] : null;
  for (const event of data.events) {
    await useCallHistoryStore.getState().upsertServerEvent({
      ...event,
      serverId: identity?.serverId,
      serverName: identity?.serverName || identity?.serverId,
      serverUrl: identity?.serverUrl
    });
  }
}

export async function requestCallHistorySync(since?: number): Promise<void> {
  const httpContext = getCallHistoryHttpContext();
  if (!httpContext) return;
  const payload = typeof since === 'number' && Number.isFinite(since) ? { since } : {};
  const isWsReady = wsClient.isConnectedToServer(httpContext.identity.serverUrl || '')
    && wsClient.getConnectionContext()?.deviceId === httpContext.deviceId;
  if (!isWsReady) {
    const response = await callsAPI.listHistory(payload, httpContext.identity.identityId, httpContext.identity.serverUrl);
    if (!response.result) return;
    await applyCallHistorySyncResult(response.result, httpContext.identity.identityId);
    await finalizePagedSync(response.result, {
      acknowledge: async (syncedThrough) => {
        await callsAPI.ackHistory({ syncedThrough }, httpContext.identity.identityId, httpContext.identity.serverUrl);
      },
      continueFrom: requestCallHistorySync
    });
    return;
  }

  const context = await getCallHistoryContext(httpContext.identity.identityId);
  if (!context) {
    const response = await callsAPI.listHistory(payload, httpContext.identity.identityId, httpContext.identity.serverUrl);
    if (!response.result) return;
    await applyCallHistorySyncResult(response.result, httpContext.identity.identityId);
    await finalizePagedSync(response.result, {
      acknowledge: async (syncedThrough) => {
        await callsAPI.ackHistory({ syncedThrough }, httpContext.identity.identityId, httpContext.identity.serverUrl);
      },
      continueFrom: requestCallHistorySync
    });
    return;
  }

  const signature = await signPayloadWithKey(payload, context.identity.signingKey);
  wsClient.send({
    type: 'call-history:sync',
    data: {
      type: 'call-history:sync',
      payload,
      deviceId: context.deviceId,
      signature
    },
    timestamp: Date.now()
  });
}

async function sendCallHistorySyncAck(syncedThrough: number): Promise<void> {
  const context = await getCallHistoryContext();
  if (!context) return;
  const payload = { syncedThrough };
  const signature = await signPayloadWithKey(payload, context.identity.signingKey);
  const data: WSCallHistorySyncAckData = {
    type: 'call-history:sync-ack',
    payload,
    deviceId: context.deviceId,
    signature
  };
  wsClient.send({
    type: 'call-history:sync-ack',
    data,
    timestamp: Date.now()
  });
}

export async function sendCallConnected(
  callSessionId: string,
  connectedAt: number,
  options: { localIdentityId?: string; serverUrl?: string } = {}
): Promise<void> {
  let localIdentityId = (options.localIdentityId || '').toString().trim();
  let serverUrl = (options.serverUrl || '').toString().trim() || undefined;
  const payload = { callSessionId, connectedAt };
  const httpContext = getCallHistoryHttpContext(localIdentityId);
  if (httpContext) {
    localIdentityId = httpContext.identity.identityId;
    serverUrl = serverUrl || httpContext.identity.serverUrl;
  }

  const isWsReady = httpContext
    && wsClient.isConnectedToServer(httpContext.identity.serverUrl || '')
    && wsClient.getConnectionContext()?.deviceId === httpContext.deviceId;
  if (isWsReady) try {
    const context = await getCallHistoryContext(localIdentityId);
    if (context) {
      localIdentityId = context.identity.identityId;
      serverUrl = serverUrl || context.identity.serverUrl;
      const signature = await signPayloadWithKey(payload, context.identity.signingKey);
      const data: WSCallConnectedData = {
        type: 'call:connected',
        payload,
        deviceId: context.deviceId,
        signature
      };
      wsClient.send({ type: 'call:connected', data, timestamp: Date.now() });
    }
  } catch {
    // Non-fatal: HTTP fallback below
  }

  if (localIdentityId) {
    await callsAPI.connected(payload, localIdentityId, serverUrl);
  }
  void requestCallHistorySync().catch(() => {});
}

export async function sendCallHeartbeat(params: {
  callSessionId: string;
  connectedAt: number;
  sentAt?: number;
}): Promise<boolean> {
  if (!wsClient.isConnected()) return false;

  const context = await getCallHistoryContext();
  if (!context) return false;

  const payload = {
    callSessionId: params.callSessionId,
    connectedAt: params.connectedAt,
    sentAt: typeof params.sentAt === 'number' && Number.isFinite(params.sentAt)
      ? Math.floor(params.sentAt)
      : Date.now()
  };
  const signature = await signPayloadWithKey(payload, context.identity.signingKey);
  const data: WSCallHeartbeatData = {
    type: 'call:heartbeat',
    payload,
    deviceId: context.deviceId,
    signature
  };
  return wsClient.sendIfReady({ type: 'call:heartbeat', data, timestamp: Date.now() });
}

export async function sendCallFinalized(params: {
  callSessionId: string;
  endedAt: number;
  finalStatus: 'answered' | 'rejected' | 'ended' | 'failed';
  durationSeconds?: number;
  reason?: string;
  localIdentityId?: string;
  serverUrl?: string;
}): Promise<void> {
  let localIdentityId = (params.localIdentityId || '').toString().trim();
  let serverUrl = (params.serverUrl || '').toString().trim() || undefined;
  const payload = {
    callSessionId: params.callSessionId,
    endedAt: params.endedAt,
    finalStatus: params.finalStatus,
    durationSeconds: params.durationSeconds,
    reason: params.reason
  };
  const httpContext = getCallHistoryHttpContext(localIdentityId);
  if (httpContext) {
    localIdentityId = httpContext.identity.identityId;
    serverUrl = serverUrl || httpContext.identity.serverUrl;
  }

  const isWsReady = httpContext
    && wsClient.isConnectedToServer(httpContext.identity.serverUrl || '')
    && wsClient.getConnectionContext()?.deviceId === httpContext.deviceId;
  if (isWsReady) try {
    const context = await getCallHistoryContext(localIdentityId);
    if (context) {
      localIdentityId = context.identity.identityId;
      serverUrl = serverUrl || context.identity.serverUrl;
      const signature = await signPayloadWithKey(payload, context.identity.signingKey);
      const data: WSCallFinalizedData = {
        type: 'call:finalized',
        payload,
        deviceId: context.deviceId,
        signature
      };
      wsClient.send({ type: 'call:finalized', data, timestamp: Date.now() });
    }
  } catch {
    // Non-fatal: HTTP fallback below
  }

  if (localIdentityId) {
    await callsAPI.finalized(payload, localIdentityId, serverUrl);
  }
  void requestCallHistorySync().catch(() => {});
}

export async function sendMissedCallsSeen(params: {
  localIdentityId?: string;
  callSessionIds?: string[];
  peerIdentityId?: string;
  seenAt?: number;
}): Promise<void> {
  const httpContext = getCallHistoryHttpContext(params.localIdentityId);
  if (!httpContext) return;

  const callSessionIds = Array.from(new Set((params.callSessionIds || [])
    .map((id) => String(id || '').trim())
    .filter(Boolean)));
  const peerIdentityId = String(params.peerIdentityId || '').trim();
  if (callSessionIds.length === 0 && !peerIdentityId) return;

  const payload = {
    ...(callSessionIds.length > 0 ? { callSessionIds } : {}),
    ...(peerIdentityId ? { peerIdentityId } : {}),
    seenAt: typeof params.seenAt === 'number' && Number.isFinite(params.seenAt)
      ? Math.floor(params.seenAt)
      : Date.now()
  };

  const isWsReady = wsClient.isConnectedToServer(httpContext.identity.serverUrl || '')
    && wsClient.getConnectionContext()?.deviceId === httpContext.deviceId;
  if (isWsReady) {
    try {
      const context = await getCallHistoryContext(httpContext.identity.identityId);
      if (context) {
        const signature = await signPayloadWithKey(payload, context.identity.signingKey);
        const data: WSCallHistoryMarkMissedSeenData = {
          type: 'call-history:mark-missed-seen',
          payload,
          deviceId: context.deviceId,
          signature
        };
        wsClient.send({ type: 'call-history:mark-missed-seen', data, timestamp: Date.now() });
      }
    } catch {
      // Non-fatal: HTTP fallback below
    }
  }
  await callsAPI.markMissedSeen(payload, httpContext.identity.identityId, httpContext.identity.serverUrl);
  void requestCallHistorySync().catch(() => {});
}

export function initializeCallHistorySync() {
  if (initialized) return;
  initialized = true;

  wsClient.on('registered', () => {
    if (wsClient.isExternalMode()) return;
    void requestCallHistorySync().catch(() => {});
  });

  wsClient.on('call-history:sync-result', async (message) => {
    const data = message.data as WSCallHistorySyncResultData;
    const connectionIdentityId = (wsClient.getConnectionContext()?.identityId || '').toString().trim();
    await applyCallHistorySyncResult(data, connectionIdentityId);

    await finalizePagedSync(data, {
      acknowledge: sendCallHistorySyncAck,
      continueFrom: requestCallHistorySync
    });
  });
}
